import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Trophy, ChevronDown, Check, Clock, AlertCircle } from 'lucide-react';

const API = `${window.location.origin}/api`;

/**
 * Dropdown that lists every month the signed-in ambassador finished as the
 * #1 Top Seller. Selecting a row navigates to that month's certificate so
 * the ambassador can re-download an old certificate or check the claim
 * status of a past prize.
 *
 * Renders nothing if:
 *   • the user isn't an ambassador (API returns 401/403)
 *   • the ambassador has zero wins yet
 *
 * Each row shows:
 *   • Month label
 *   • Bottles + revenue
 *   • Claim status badge (Sent ✓ / Pending / Expired / Available · N days)
 *
 * The currently-viewed month is marked with a check mark so the user
 * always knows where they are.
 */
export default function MyWinsPicker({ currentYear, currentMonth }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    axios.get(`${API}/ambassador/me/won-months?lookback=24`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => setItems(r.data?.items || []))
      .catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (items.length === 0) return null;

  const statusOf = (win) => {
    if (win.claim_status === 'fulfilled') return { label: 'Sent ✓', tone: 'text-[#2E9B50]' };
    if (win.claim_status === 'pending') return { label: 'Requested', tone: 'text-[#D4AF37]' };
    if (win.claim_status === 'cancelled') return { label: 'Cancelled', tone: 'text-red-400' };
    if (win.claim_expired) return { label: 'Expired', tone: 'text-white/30' };
    return { label: `${win.claim_days_left}d left`, tone: 'text-[#F59E0B]' };
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        data-testid="my-wins-picker-btn"
        className="flex items-center gap-2 px-4 py-2 border border-[#D4AF37]/40 text-[#D4AF37] text-xs uppercase tracking-[0.2em] hover:bg-[#D4AF37]/10 transition-colors font-sans"
      >
        <Trophy className="w-3.5 h-3.5" />
        My Wins
        <span className="bg-[#D4AF37] text-black text-[10px] px-1.5 py-0.5 ml-0.5">{items.length}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          data-testid="my-wins-picker-menu"
          className="absolute right-0 top-full mt-1.5 w-80 max-h-96 overflow-y-auto bg-[#0A0A0A] border border-[#D4AF37]/30 shadow-2xl z-50"
        >
          <div className="sticky top-0 bg-[#0A0A0A] border-b border-white/5 px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-sans">Top-Seller months</p>
            <p className="text-white/40 text-[11px] font-sans mt-0.5">Tap any month to view or re-download your certificate.</p>
          </div>
          {items.map(win => {
            const status = statusOf(win);
            const isCurrent = win.year === currentYear && win.month === currentMonth;
            return (
              <button
                key={`${win.year}-${win.month}`}
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate(`/certificate/${win.year}/${win.month}`);
                }}
                data-testid={`my-wins-row-${win.year}-${win.month}`}
                className={`w-full text-left px-3 py-3 border-b border-white/5 hover:bg-white/5 transition-colors ${
                  isCurrent ? 'bg-[#D4AF37]/10' : ''
                }`}
              >
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-white text-sm font-sans font-medium">{win.month_label}</p>
                      {isCurrent && <Check className="w-3.5 h-3.5 text-[#D4AF37]" />}
                    </div>
                    <p className="text-white/45 text-[11px] font-sans mt-0.5">
                      {win.bottles} bottles · ₱{Number(win.revenue).toLocaleString()}
                    </p>
                  </div>
                  <div className={`flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-sans whitespace-nowrap ${status.tone}`}>
                    {win.claim_expired
                      ? <AlertCircle className="w-3 h-3" />
                      : win.claim_status === 'fulfilled'
                        ? <Check className="w-3 h-3" />
                        : <Clock className="w-3 h-3" />}
                    {status.label}
                  </div>
                </div>
              </button>
            );
          })}
          <div className="px-3 py-2 text-[10px] text-white/35 font-sans bg-[#141414]">
            Past certificates remain downloadable forever — only the cash claim has a 7-day window.
          </div>
        </div>
      )}
    </div>
  );
}
